import { modelOptions, post, prop } from "@typegoose/typegoose";
import { getZodSchemaFromClass, validate } from "../../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../../schemaBase.js";
import type {
    SproutVerificationIndexFieldsWithDefaults,
    SproutVerificationIndexSchemaTypes,
} from "../../../types/sproutVerificationIndex.d.js";
import { type DtoValidatorTypes } from "../../../validatableClass.js";
import { SproutVerificationInstance } from "./sproutVerificationInstance.js";

const SCHEMA_KEY = "SproutVerificationIndex" as const;

/**
 * Index that maps valve state combinations to their corresponding verification instances.
 *
 * Valve states are represented as binary combinations where each bit represents a valve:
 * - 0b00: Both valves closed
 * - 0b01: Valve 0 closed, Valve 1 open
 * - 0b10: Valve 0 open, Valve 1 closed
 * - 0b11: Both valves open
 *
 * Each valve state can have multiple verification instances over time, tracking the
 * irrigation system's performance in each configuration.
 *
 * @see SproutVerificationInstance - Individual verification records stored at each index
 */
// TODO: convert to responseOnly schema class
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
    },
})
@post<SchemaBase<SproutVerificationIndex>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SproutVerificationIndex extends SchemaBase<
    SproutVerificationIndex,
    SproutVerificationIndexFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /** Verification instance for valve state 0b00 (both valves closed) */
    @validate({
        childClass: SproutVerificationInstance,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => SproutVerificationInstance, required: false })
    public 0b00?: SproutVerificationInstance;

    /** Verification instance for valve state 0b01 (valve 0 closed, valve 1 open) */
    @validate({
        childClass: SproutVerificationInstance,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => SproutVerificationInstance, required: false })
    public 0b01?: SproutVerificationInstance;

    /** Verification instance for valve state 0b10 (valve 0 open, valve 1 closed) */
    @validate({
        childClass: SproutVerificationInstance,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => SproutVerificationInstance, required: false })
    public 0b10?: SproutVerificationInstance;

    /** Verification instance for valve state 0b11 (both valves open) */
    @validate({
        childClass: SproutVerificationInstance,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => SproutVerificationInstance, required: false })
    public 0b11?: SproutVerificationInstance;

    static getters = {} as const satisfies Record<string, (self: SproutVerificationIndex) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SproutVerificationIndexSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<SproutVerificationIndex, SproutVerificationIndexFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

SproutVerificationIndex.dtoValidators = getZodSchemaFromClass(
    SproutVerificationIndex,
) as unknown as DtoValidatorTypes<SproutVerificationIndexSchemaTypes>;
